// These schemas will eventually come from a TS client generated from vrs-service, but
// that doesn't exist yet.
import { z as zod } from "zod";

export const idealLine = zod.object({
  maxSeedingRate: zod.number(),
  minSeedingRate: zod.number(),
  minExtrapolatedSeedingRate: zod.number().optional(),
  maxYieldTarget: zod.number(),
  minYieldTarget: zod.number(),
  minExtrapolatedYieldTarget: zod.number().optional(),
  method: zod.string(),
});
export const getIdealLineResponse = zod.object({
  data: idealLine,
});

export const baseMachineType = zod.object({
  machineName: zod.string(),
  machineDisplayName: zod.string(),
});
export const machineType = baseMachineType.extend({
  supportedFeatures: zod.array(zod.string()).nullish(),
});

export const getV3ExportsConfigurationsResponse = zod.array(machineType);

export const seedingCurveDefaultsCorn = zod.object({
  standLoss: zod.number(),
  seedCost: zod.number(),
  grainPrice: zod.number(),
  minExtrapolatedSeedingRate: zod.number().optional(),
  minSeedingRate: zod.number(),
  maxSeedingRate: zod.number(),
  minExtrapolatedYieldTarget: zod.number().optional(),
  minYieldTarget: zod.number(),
  maxYieldTarget: zod.number(),
  rateIncrement: zod.number(),
  seedingCurveType: zod.string(),
});

const preferences = zod.object({
  seedId: zod.string(),
  cropSeasonId: zod.string().optional(),
});

export const seedingCurvePreferencesCorn =
  seedingCurveDefaultsCorn.merge(preferences);

export const vrsSeedDetails = zod.object({
  display_name: zod.string().nullable(),
  crop_id: zod.string().uuid().nullable(),
  manufacturer: zod.string().nullable(),
  packaging_no_of_seeds: zod.number().nullable().optional(),
  relative_maturity: zod.number().nullable(),
  base_genetics: zod.string().nullable(),
});

export const operationSeedingCurvePreferencesCorn = zod.object({
  defaults: seedingCurveDefaultsCorn,
  preferences: zod.array(seedingCurvePreferencesCorn),
  seedDetails: zod.record(zod.string(), vrsSeedDetails).optional(),
});

export const getV4OperationSeedingCurvePreferencesCornResponse =
  operationSeedingCurvePreferencesCorn;

export const seedingCurveDefaultsSoybeans = zod.object({
  minSeedingRate: zod.number(),
  maxSeedingRate: zod.number(),
  localSeedingRate: zod.number().nullish(),
  rateIncrement: zod.number(),
});

export const seedingCurvePreferencesSoybeans =
  seedingCurveDefaultsSoybeans.merge(preferences);

export const operationSeedingCurvePreferencesSoybeans = zod.object({
  defaults: seedingCurveDefaultsSoybeans,
  preferences: zod.array(seedingCurvePreferencesSoybeans),
  seedDetails: zod.record(zod.string(), vrsSeedDetails).optional(),
});

export const getV4OperationSeedingCurvePreferencesSoybeansResponse =
  operationSeedingCurvePreferencesSoybeans;

export const deleteOperationSeedingCurvePreferencesResponse = zod.string();

const metaPrescriptionRecord = zod.object({
  recordId: zod.string().uuid(),
  productId: zod.string().uuid(),
});
const deleteMetaItem = zod.object({
  activityId: zod.string().uuid(),
  layerReferenceId: zod.string().uuid(),
  prescriptionRecords: zod.array(metaPrescriptionRecord),
});
export const getDeleteSeedingCurvePreferencesStatusResponse = zod.object({
  jobId: zod.string().uuid().optional(),
  hashKey: zod.string().optional(),
  transactionId: zod.string().uuid().optional(),
  seedIds: zod.array(zod.string().uuid()).optional(),
  cropType: zod.string().optional(),
  cropYear: zod.number().optional(),
  status: zod.string(),
  step: zod.string().optional(),
  statusCode: zod.number().nullable(),
  meta: zod.array(deleteMetaItem).optional(),
  errorMessage: zod.string().optional(),
});

export const statsItem = zod.object({
  activityId: zod.string().uuid(),
  recordId: zod.string().uuid(),
  minCalcSeedingRate: zod.number(),
  maxCalcSeedingRate: zod.number(),
  avgCalcSeedingRate: zod.number(),
  minSeedingRate: zod.number(),
  maxSeedingRate: zod.number(),
  avgSeedingRate: zod.number(),
  calcUnits: zod.number(), // This is number of bags.
  units: zod.number(), // This is number of bags.
});

export const getV4PrescriptionStatsResponse = zod.object({
  stats: zod.array(statsItem),
});

export const recordPrescriptionPreferencesCorn = zod.object({
  activityId: zod.string().uuid().nullable(),
  recordId: zod.string().uuid().nullable(),
  zoneSetId: zod.string().nullable(),
  zoneSetRevisionId: zod.string().nullable(),
  seedId: zod.string().nullable(),
  rateIncrement: zod.number().nullable(),
  seedCost: zod.number().nullable(),
  grainPrice: zod.number().nullable(),
  standLoss: zod.number().nullable(),
  minYieldTarget: zod.number().nullable(),
  maxYieldTarget: zod.number().nullable(),
  minSeedingRate: zod.number().nullable(),
  maxSeedingRate: zod.number().nullable(),
  drylandYieldGoal: zod.number().nullable(),
  irrYieldGoal: zod.number().nullable(),
  yieldGoalUnitOfMeasure: zod.string().nullable(),
  seedingCurveType: zod.string(),
});

export const recordPrescriptionPreferencesSoybeans = zod.object({
  activityId: zod.string().nullable(),
  recordId: zod.string().nullable(),
  zoneSetId: zod.string().nullable(),
  zoneSetRevisionId: zod.string().nullable(),
  seedId: zod.string().nullable(),
  rateIncrement: zod.number().nullable(),
  localSeedingRate: zod.number().nullable(),
  minSeedingRate: zod.number().nullable(),
  maxSeedingRate: zod.number().nullable(),
  drylandYieldGoal: zod.number().nullable(),
  irrYieldGoal: zod.number().nullable(),
  yieldGoalUnitOfMeasure: zod.string().nullable(),
});

export const irrigatedSettings = zod.object({
  seedingCurveType: zod.string().optional(),
  minSeedingRate: zod.number().optional(),
  maxSeedingRate: zod.number().optional(),
  rateIncrement: zod.number().optional(),
  minYieldTarget: zod.number().optional(),
  maxYieldTarget: zod.number().optional(),
  seedCost: zod.number().optional(),
  standLoss: zod.number().optional(),
  grainPrice: zod.number().optional(),
});
export const drylandSettings = irrigatedSettings.merge(
  zod.object({ localSeedingRate: zod.number().nullish(),
}));

export const seedingOptimizer = zod.object({
  type: zod.string(),
  zoneSet: zod.string().uuid().nullish(),
  preferredUnitSystem: zod.string(),
});

export const varietyInput = zod.object({
  seedId: zod.string().uuid(),
  cropId: zod.string().uuid(),
  drylandSettings: drylandSettings.optional(),
  irrigatedSettings: irrigatedSettings.optional(),
  isBackup: zod.boolean(),
});

export const prescriptionInput = zod.object({
  activityId: zod.string().uuid(),
  description: zod.string(),
  seedId: zod.string().uuid(),
  treatmentId: zod.string().uuid().nullish(),
  seedingOptimizer: seedingOptimizer,
  fieldId: zod.string().uuid(),
  cropId: zod.string().uuid(),
  eventDate: zod.string().nullish(),
  cropSeasonId: zod.string().uuid(),
});

export const runVrsRequest = zod.object({
  varietyInputs: zod.array(varietyInput),
  prescriptionInputs: zod.array(prescriptionInput),
  isFlatRate: zod.boolean().optional(),
});

/**
 * A prescription input keyed by planting plan, for the /v6 run route. Same shape
 * as `prescriptionInput` with the planting plan in place of the activity — the
 * planning paradigm has no activity to name, so `description` no longer comes
 * from one.
 */
export const planningPrescriptionInput = zod.object({
  plantingPlanId: zod.string().uuid(),
  description: zod.string(),
  seedId: zod.string().uuid(),
  treatmentId: zod.string().uuid().nullish(),
  seedingOptimizer: seedingOptimizer,
  fieldId: zod.string().uuid(),
  cropId: zod.string().uuid(),
  eventDate: zod.string().nullish(),
  cropSeasonId: zod.string().uuid(),
});

export const runVrsPlanningRequest = zod.object({
  varietyInputs: zod.array(varietyInput),
  prescriptionInputs: zod.array(planningPrescriptionInput),
  isFlatRate: zod.boolean().optional(),
});

export const rerunVrsRequest = zod.object({
  seedId: zod.string(),
  cropSeasonId: zod.string(),
  preferredUnitSystem: zod.string(),
  isFlatRate: zod.boolean().optional(),
});

export const runVrsResponse = zod.string(); // The command ID
export const rerunVrsResponse = zod.string(); // The command ID

export const runAutoVrsResponse = zod.object({
  successful: zod.array(zod.string()),
  failed: zod.array(zod.string()),
});

export const vrsPollResult = zod.object({
  createdBy: zod.string(),
  commandId: zod.string(),
  parentCommandId: zod.string().nullable(),
  optionalCorrelationId: zod.string().nullable(),
  status: zod.string(),
});

export const vrsGetStatusesForCommandResponse = zod.array(vrsPollResult);

const sendTos = zod.object({
  download: zod.boolean(),
  emailAddresses: zod.string().array(),
  emailSubject: zod.string(),
  emailMessage: zod.string(),
  deereMachineIds: zod.string().array(),
  deereOpsCenter: zod.boolean(),
  deereWorkPlan: zod.boolean(),
});
export const airSeederConversionRecord = zod.object({
  recordId: zod.string(),
  productId: zod.string(),
  conversionFactor: zod.number(),
});
export const vrsExportData = zod.object({
  records: zod.string().array(),
  sendTos,
  configurations: baseMachineType,
  prescriptionRateType: zod.string(),
  mergeExportedFilesPerField: zod.boolean(),
  airSeederConversion: zod.array(airSeederConversionRecord).optional(),
  preferredUnitSystem: zod.string().optional(),
  includeReport: zod.boolean(),
  includePrimaryVarietiesOnly: zod.boolean(),
  exportType: zod.string(),
});
export const exportVrsResponse = zod.object({
  exportIds: zod.string().array(),
});

// The v4 pair-based export payload (TRA-322).
//
// Verified against vrs-service `VariableRateSeedingExportRequestV2`. The flat
// `records` list of activities record ids becomes `prescriptionInputs`, one
// (plantingPlanId, inputId) pair per variety per plan — the same grain as v3's
// one record id per variety per field. Everything else on the body is unchanged,
// which is why the history GETs need no new version.
//
// The service rejects three things with a 422, so the builder must not produce
// them: an empty `prescriptionInputs`, a duplicated (plantingPlanId, inputId)
// pair, and a duplicated air-seeder `inputId`.
export const vrsExportPlanInput = zod.object({
  plantingPlanId: zod.string(),
  inputId: zod.string(),
});
/**
 * `AirSeederConversionInfoV2` — `recordId` is gone and v3's `productId` is now
 * `inputId`: the same UUID under the planning vocabulary, so there is no second
 * id to send.
 */
export const airSeederConversionInput = zod.object({
  inputId: zod.string(),
  conversionFactor: zod.number(),
});
export const vrsExportDataV4 = zod.object({
  prescriptionInputs: zod.array(vrsExportPlanInput),
  sendTos,
  configurations: baseMachineType,
  prescriptionRateType: zod.string(),
  mergeExportedFilesPerField: zod.boolean(),
  airSeederConversion: zod.array(airSeederConversionInput).optional(),
  preferredUnitSystem: zod.string().optional(),
  includeReport: zod.boolean(),
  includePrimaryVarietiesOnly: zod.boolean(),
  exportType: zod.string(),
});

// vrs-service `VariableRateSeedingReportRequest`: the summary report without an
// export. It is keyed on whole planting plans - the service reads every variety on
// each one - and rejects an empty or duplicated `plantingPlanIds` with a 422.
export const vrsReportData = zod.object({
  plantingPlanIds: zod.array(zod.string()),
  includePrimaryVarietiesOnly: zod.boolean(),
  airSeederConversion: zod.array(airSeederConversionInput).optional(),
  preferredUnitSystem: zod.string().optional(),
});
export const generateVrsReportResponse = zod.object({
  reportId: zod.string(),
});

// VrsCropAssignmentSummary
export const inputComponentReference = zod.object({
  id: zod.string().uuid().nullish(),
  inputId: zod.string().uuid(),
  sum: zod.number(),
  unit: zod.string(),
  area: zod.number(),
});
export const recordReference = zod.object({
  recordId: zod.string().uuid(),
  date: zod.string().nullish(),
  isActive: zod.boolean(),
  isBestInRecordType: zod.boolean(),
  inputComponents: zod.array(inputComponentReference),
});
const primaryVarietySummary = zod.object({
  inputId: zod.string().uuid(),
  treatmentId: zod.string().uuid().nullish(),
});
export const vrsCropAssignmentSummary = zod.object({
  operationId: zod.string().uuid(),
  cropAssignmentId: zod.string().uuid(),
  cropId: zod.string().uuid(),
  cropSeasonId: zod.string().uuid(),
  fieldId: zod.string().uuid(),
  activityId: zod.string().uuid().nullish(),
  activityName: zod.string().nullish(),
  primaryVarieties: zod.array(primaryVarietySummary),
  hasVrs: zod.boolean(),
  isCompleted: zod.boolean(),
  prescriptionRecordsReference: zod.array(recordReference),
  varietyRecordMap: zod.record(zod.string().uuid(), zod.string().uuid()),
  inputIds: zod.array(zod.string().uuid()),
});
export const vrsCropAssignmentSummaryResponse = zod.object({
  cropAssignmentSummaries: zod.array(vrsCropAssignmentSummary),
});

// VrsFieldPlanSummary — the planning-keyed v6 counterpart (TRA-321).
//
// `GET /v6/seeding/vrs/field-plan-summaries/<common_cycle_id>` replaces the v5
// crop-assignment summaries above. Verified against vrs-service's
// `VrsFieldPlanSummaryV6` / `PrescriptionInputReference`; casing is camelCase on
// the wire (pydantic `to_camel`).
//
// Rows are keyed on `plantingPlanId` and carry no activities identifier. Three
// v5 fields are deliberately absent, not optional:
//
//   - `varietyRecordMap` — its two uses (does this variety have a record, and
//     which id do I send back) are both answered by `prescriptionInputs`.
//   - `sum` / `unit` / `area` on a prescription — v5 carried a total that *was*
//     avgRate x area; v6 carries the rates themselves.
//   - `date` / `isBestInRecordType` — the first is vestigial, the second ranks
//     records within an activity and planning has one row per variety.
//
// `isCompleted` survives and keeps its activities-derived meaning: MACHINE and
// MANUAL records stay in activities permanently, but it is resolved on
// (fieldId, cropSeasonId) so no activity id reaches the response.
//
// The six rates and both bag counts are the whole of what
// `GET /v4/seeding/prescriptionstats` supplied through a separate record-keyed
// request, so this needs no second fetch and no retry loop:
//
//   userEnteredBags -> v5 statsItem.units
//   calculatedBags  -> v5 statsItem.calcUnits
export const vrsFieldPlanPrescriptionInput = zod.object({
  plantingPlanId: zod.string().uuid(),
  inputId: zod.string().uuid(),
  isPrimary: zod.boolean(),
  avgSeedingRate: zod.number(),
  minSeedingRate: zod.number(),
  maxSeedingRate: zod.number(),
  avgCalcSeedingRate: zod.number(),
  minCalcSeedingRate: zod.number(),
  maxCalcSeedingRate: zod.number(),
  seedingRateUnitOfMeasure: zod.string(),
  userEnteredBags: zod.number(),
  calculatedBags: zod.number(),
});
export const vrsFieldPlanSummary = zod.object({
  operationId: zod.string().uuid(),
  cropId: zod.string().uuid(),
  cropSeasonId: zod.string().uuid(),
  fieldId: zod.string().uuid(),
  plantingPlanId: zod.string().uuid(),
  primaryVarieties: zod.array(primaryVarietySummary),
  hasVrs: zod.boolean(),
  isCompleted: zod.boolean(),
  prescriptionInputs: zod.array(vrsFieldPlanPrescriptionInput),
  inputIds: zod.array(zod.string().uuid()),
});
export const vrsFieldPlanSummaryResponse = zod.object({
  fieldPlanSummaries: zod.array(vrsFieldPlanSummary),
  seedDetails: zod.record(zod.string(), vrsSeedDetails).optional(),
});

// Export history schemas
export const exportHistoryItem = zod.object({
  atlasExportId: zod.string().uuid().optional().nullable(),
  exportId: zod.string().uuid(),
  // Note: Using string instead of datetime() to match API behavior
  // datetime() validation was causing crashes even with valid strings
  exportDate: zod.string(),
  originalUserEmail: zod.string(),
  originalUserName: zod.string(),
  operationId: zod.string().uuid(),
  machineName: zod.string(),
  machineDisplayName: zod.string(),
  status: zod.string().optional().nullable(),
  storageLocation: zod.string().optional().nullable(),
  presignedUrl: zod.string().optional().nullable(),
  sendTosEmail: zod.array(zod.string()),
  sendTosDeere: zod.array(zod.string()),
  sendTosDownload: zod.array(zod.string()),
  fieldCount: zod.number(),
  presignedReportUrl: zod.string().optional().nullable(),
  reportStatus: zod.string().optional().nullable()
});

export const getV3ExportsHistoryResponse = zod.object({
  exportsHistory: zod.array(exportHistoryItem)
});
